import { Mail, Map, Shield, Smartphone, UserRoundX } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { api } from "../api/client";
import { track, trackPerformance } from "../api/telemetry";
import type {
  AspectRatio,
  DeliveryChannel,
  MapImageryType,
  MapProvider,
  PreferencesResponse,
  WatermarkPosition
} from "../types";

const fallbackPreferences: PreferencesResponse = {
  strava_authenticated: false,
  integration_enabled: false,
  aspect_ratio: "square",
  map_provider: "ordnance_survey",
  imagery_type: "default",
  activity_types: ["Run"],
  delivery_channel: "email",
  masked_destination: "",
  verification_status: null,
  privacy_disclosure_accepted: false,
  privacy_policy_accepted: false,
  provider_terms_accepted: false,
  delivery_consent_accepted: false,
  disclosure_version: "",
  trim_start_meters: 0,
  trim_end_meters: 0,
  route_colour: "#ff2d55",
  watermark_text: "Custom activity maps",
  watermark_position: "bottom_right",
  warnings: []
};

export function App() {
  const [preferences, setPreferences] = useState<PreferencesResponse>(fallbackPreferences);
  const [contactDestination, setContactDestination] = useState("");
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [status, setStatus] = useState("Loading configuration...");

  useEffect(() => {
    const started = performance.now();
    api
      .getPreferences()
      .then((response) => {
        setPreferences(response);
        setStatus("Configuration loaded");
        void track("app_loaded", { page: "configuration", status: "api_loaded" });
        void trackPerformance("configuration_load_ms", performance.now() - started);
      })
      .catch(() => {
        setStatus("Using local defaults until the API is connected");
        void track("client_error", { page: "configuration", reason_code: "preferences_load_failed" });
      });
  }, []);

  async function savePreferences(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void track("preferences_save_started", {
      aspect_ratio: preferences.aspect_ratio,
      delivery_channel: preferences.delivery_channel,
      map_provider: preferences.map_provider
    });
    try {
      const response = await api.updatePreferences({
        integration_enabled: preferences.integration_enabled,
        aspect_ratio: preferences.aspect_ratio,
        map_provider: preferences.map_provider,
        imagery_type: preferences.imagery_type,
        activity_types: preferences.activity_types,
        trim_start_meters: preferences.trim_start_meters,
        trim_end_meters: preferences.trim_end_meters,
        delivery_channel: preferences.delivery_channel,
        route_colour: preferences.route_colour,
        watermark_text: preferences.watermark_text,
        watermark_position: preferences.watermark_position
      });
      setPreferences(response);
      setStatus("Preferences saved");
      void track("preferences_save_succeeded", { status: "success" });
    } catch {
      setStatus("Preferences could not be saved");
      void track("preferences_save_failed", { reason_code: "api_error" });
    }
  }

  async function acceptPrivacy() {
    const response = await api.acceptPrivacyDisclosure();
    setPreferences(response);
    setStatus("Required disclosures accepted");
    void track("privacy_disclosure_accepted", { status: "accepted" });
  }

  async function saveContact(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void track("contact_save_started", { delivery_channel: preferences.delivery_channel });
    try {
      const response = await api.updateContact(preferences.delivery_channel, contactDestination);
      setPreferences(response);
      setContactDestination("");
      setStatus("Contact saved. Verification is required before delivery is enabled.");
      void track("contact_save_succeeded", { delivery_channel: preferences.delivery_channel });
    } catch {
      setStatus("Contact could not be saved");
      void track("contact_save_failed", { delivery_channel: preferences.delivery_channel });
    }
  }

  async function joinWaitlist(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await api.joinWaitlist(waitlistEmail);
    setWaitlistEmail("");
    setStatus("Waitlist request submitted");
    void track("waitlist_submitted", { status: "submitted" });
  }

  if (!preferences.strava_authenticated) {
    return (
      <main className="app-shell">
        <header className="topbar">
          <div>
            <h1>Custom activity maps</h1>
          </div>
          <a
            className="primary-action"
            href="/api/oauth/strava/start"
            onClick={() => void track("strava_link_clicked", { page: "welcome" })}
          >
            Link Strava
          </a>
        </header>

        <section className="intro-panel">
          <p>
            Custom activity maps creates privacy-aware route images from your Strava activities using
            configurable map imagery, route styling, and delivery settings after you connect Strava.
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <h1>Custom activity maps</h1>
          <p>{status}</p>
        </div>
        <a
          className="primary-action"
          href="/api/oauth/strava/start"
          onClick={() => void track("strava_link_clicked", { page: "configuration" })}
        >
          Link Strava
        </a>
      </header>

      <section className="layout">
        <form className="panel" onSubmit={savePreferences}>
          <h2>
            <Map size={20} /> Map delivery
          </h2>
          <label>
            <span>Integration</span>
            <select
              value={preferences.integration_enabled ? "enabled" : "disabled"}
              onChange={(event) =>
                setPreferences({
                  ...preferences,
                  integration_enabled: event.target.value === "enabled"
                })
              }
            >
              <option value="disabled">Disabled</option>
              <option value="enabled">Enabled</option>
            </select>
          </label>
          <label>
            <span>Aspect ratio</span>
            <select
              value={preferences.aspect_ratio}
              onChange={(event) =>
                setPreferences({ ...preferences, aspect_ratio: event.target.value as AspectRatio })
              }
            >
              <option value="square">Square</option>
              <option value="16:9">16:9</option>
            </select>
          </label>
          <label>
            <span>Map provider</span>
            <select
              value={preferences.map_provider}
              onChange={(event) =>
                setPreferences({ ...preferences, map_provider: event.target.value as MapProvider })
              }
            >
              <option value="ordnance_survey">Ordnance Survey</option>
            </select>
          </label>
          <label>
            <span>Map imagery</span>
            <select
              value={preferences.imagery_type}
              onChange={(event) =>
                setPreferences({ ...preferences, imagery_type: event.target.value as MapImageryType })
              }
            >
              <option value="default">Default</option>
              <option value="mastermap">MasterMap</option>
              <option value="explorer_25000">Explorer 1:25000</option>
              <option value="landranger_50000">Landranger 1:50000</option>
            </select>
          </label>
          {preferences.imagery_type === "explorer_25000" ? (
            <p className="muted">
              Explorer scale can require more tiles for long routes, so very long activities may fail safely.
            </p>
          ) : null}
          <label>
            <span>Delivery</span>
            <select
              value={preferences.delivery_channel}
              onChange={(event) =>
                setPreferences({
                  ...preferences,
                  delivery_channel: event.target.value as DeliveryChannel
                })
              }
            >
              <option value="email">Email via SES</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </label>
          <label>
            <span>Route colour</span>
            <input
              type="color"
              value={preferences.route_colour}
              onChange={(event) =>
                setPreferences({ ...preferences, route_colour: event.target.value })
              }
            />
          </label>
          <div className="split-fields">
            <label>
              <span>Trim start (m)</span>
              <input
                min="0"
                type="number"
                value={preferences.trim_start_meters}
                onChange={(event) =>
                  setPreferences({
                    ...preferences,
                    trim_start_meters: Number(event.target.value)
                  })
                }
              />
            </label>
            <label>
              <span>Trim end (m)</span>
              <input
                min="0"
                type="number"
                value={preferences.trim_end_meters}
                onChange={(event) =>
                  setPreferences({
                    ...preferences,
                    trim_end_meters: Number(event.target.value)
                  })
                }
              />
            </label>
          </div>
          <label>
            <span>Watermark text</span>
            <input
              value={preferences.watermark_text}
              onChange={(event) =>
                setPreferences({ ...preferences, watermark_text: event.target.value })
              }
            />
          </label>
          <label>
            <span>Watermark position</span>
            <select
              value={preferences.watermark_position}
              onChange={(event) =>
                setPreferences({
                  ...preferences,
                  watermark_position: event.target.value as WatermarkPosition
                })
              }
            >
              <option value="bottom_right">Bottom right</option>
              <option value="bottom_left">Bottom left</option>
              <option value="top_right">Top right</option>
              <option value="top_left">Top left</option>
            </select>
          </label>
          <button type="submit">Save preferences</button>
        </form>

        <form className="panel" onSubmit={saveContact}>
          <h2>
            {preferences.delivery_channel === "email" ? <Mail size={20} /> : <Smartphone size={20} />}
            Contact details
          </h2>
          <p className="muted">
            Stored only for image delivery, encrypted at rest, and masked everywhere after saving.
          </p>
          <label>
            <span>{preferences.delivery_channel === "email" ? "Email address" : "WhatsApp number"}</span>
            <input
              value={contactDestination}
              placeholder={preferences.delivery_channel === "email" ? "runner@example.com" : "+447700900123"}
              onChange={(event) => setContactDestination(event.target.value)}
            />
          </label>
          <dl>
            <dt>Saved destination</dt>
            <dd>{preferences.masked_destination || "None"}</dd>
            <dt>Verification</dt>
            <dd>{preferences.verification_status || "not set"}</dd>
          </dl>
          <button type="submit">Save contact</button>
        </form>

        <section className="panel">
          <h2>
            <Shield size={20} /> Privacy
          </h2>
          <p>
            Strava can provide full route data to authorized apps, including imported and private activities.
            Custom activity maps trims your configured start and end distances before rendering, stores delivery
            contact details as encrypted PII, and sends generated images to the contact you verify.
          </p>
          <p className="muted">
            Map imagery remains subject to the selected provider terms and attribution. Generated images are
            short-lived delivery artefacts, not bulk map downloads.
          </p>
          <dl>
            <dt>Privacy policy</dt>
            <dd>{preferences.privacy_policy_accepted ? "accepted" : "required"}</dd>
            <dt>Provider terms</dt>
            <dd>{preferences.provider_terms_accepted ? "accepted" : "required"}</dd>
            <dt>Delivery consent</dt>
            <dd>{preferences.delivery_consent_accepted ? "accepted" : "required"}</dd>
          </dl>
          <button type="button" onClick={acceptPrivacy}>
            {preferences.privacy_disclosure_accepted ? "Required disclosures accepted" : "Accept disclosures"}
          </button>
        </section>

        <form className="panel" onSubmit={joinWaitlist}>
          <h2>Waitlist</h2>
          <p className="muted">If capacity is full, join the Buttondown-backed waitlist.</p>
          <label>
            <span>Email address</span>
            <input value={waitlistEmail} onChange={(event) => setWaitlistEmail(event.target.value)} />
          </label>
          <button type="submit">Join waitlist</button>
        </form>

        <section className="panel danger">
          <h2>
            <UserRoundX size={20} /> Unlink
          </h2>
          <p>Unlinking deletes Strava credentials and delivery contact details.</p>
          <button
            type="button"
            onClick={async () => {
              await api.unlink();
              setStatus("Account unlinked");
            }}
          >
            Unlink account
          </button>
        </section>
      </section>
    </main>
  );
}
